// Third Party Imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {CHEVRON_LEFT__24, CHEVRON_RIGHT__24} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';
// Style
import './SimpleImageCarousel.less';

// helper method for panning position
const getPanningStyle = (type, panPct) => {
    if (!panPct) {
        return {
            cur: {},
            next: {},
            prev: {}
        };
    }
    switch (type) {
        case 'slide':
            return {
                cur: {transform: `translateX(${panPct}%)`},
                next: {transform: `translateX(${100 + panPct}%)`},
                prev: {transform: `translateX(${-100 + panPct}%)`}
            };
        default:
            return {
                cur: {},
                next: {},
                prev: {}
            };
    }
};

class SimpleImageCarousel extends Component {
    static propTypes = {
        /** percentage changed threshold on swipe required to change image */
        changeThreshold: PropTypes.number,
        /** index */
        index: PropTypes.number.isRequired,
        /** images array */
        images: PropTypes.instanceOf(Array).isRequired,
        /** className pass through */
        className: PropTypes.string,
        /** transition duration in milliseconds */
        transitionDuration: PropTypes.number,
        /** transition style */
        transitionStyle: PropTypes.oneOf(['slide', 'slideOver', 'fade']),
        /** tab index for carousel */
        tabIndex: PropTypes.number,
        /** main image click handler */
        onClick: PropTypes.func,
        /** previous image button click handler callback */
        onPrevClick: PropTypes.func,
        /** next image button click handler callback */
        onNextClick: PropTypes.func,
        /** renders previous and next buttons. Takes {setPrevRef, setNextRef} as argument */
        renderButtons: PropTypes.func,
        /** URL to navigate to when carousel (not including nav buttons) is clicked. This URL navigation will occur after the click handler. */
        href: PropTypes.string,
        /** where to display the linked URL, if specified */
        target: PropTypes.oneOf(['_blank', '_self', '_parent', '_top'])
    };

    static defaultProps = {
        changeThreshold: 20,
        index: 0,
        images: [],
        transitionDuration: 250,
        transitionStyle: 'slide',
        tabIndex: 0,
        onClick: () => {},
        onPrevClick: () => {},
        onNextClick: () => {},
        renderButtons: ({setPrevRef, setNextRef}) => {
            return [
                <button
                    key="0"
                    className="SimpleImageCarousel__button SimpleImageCarousel__button--left"
                    data-wdio="SimpleImageCarousel__button--prev"
                    ref={setPrevRef}
                >
                    <span className="sr-only">{'Previous Image'}</span>
                    <SVGIcon
                        className="SimpleImageCarousel__svg SimpleImageCarousel__svg--left"
                        markup={CHEVRON_LEFT__24}
                    />
                </button>,
                <button
                    key="1"
                    className="SimpleImageCarousel__button SimpleImageCarousel__button--right"
                    data-wdio="SimpleImageCarousel__button--next"
                    ref={setNextRef}
                >
                    <span className="sr-only">{'Next Image'}</span>
                    <SVGIcon
                        className="SimpleImageCarousel__svg SimpleImageCarousel__svg--right"
                        markup={CHEVRON_RIGHT__24}
                    />
                </button>
            ];
        }
    };

    constructor(props) {
        super(props);
        const backgroundSrc = (props.images[0] && props.images[0].src) || null;
        this.setMainRef = this.setRef.bind(this, 'main');
        this.setPrevRef = this.setRef.bind(this, 'prev');
        this.setNextRef = this.setRef.bind(this, 'next');
        this.onPrev = this.onChangeIndex.bind(this, 'prev');
        this.onNext = this.onChangeIndex.bind(this, 'next');
        this.state = {
            backgroundSrc,
            preloadSrc: '',
            transitionDirection: '',
            index: 0,
            altText: props.altText ? props.altText : '',
            panPct: 0
        };
        this.keyUp = true;
        this.timeouts = {};
        this.x = null;
        this.y = null;
        this.panAxis = null;
        this.touchMoved = false;
    }

    componentWillUnmount() {
        const {onClick} = this.props;
        /* istanbul ignore else */
        if (this.main) {
            this.main.removeEventListener('click', onClick);
            this.main.removeEventListener('keydown', this.onKeyDown);
            this.main.removeEventListener('keyup', this.onKeyUp);
            this.main.removeEventListener('touchmove', this.handleTouchMove);
            this.main.removeEventListener('touchend', this.handleTouchEnd);
        }
        /* istanbul ignore else */
        if (this.prev) {
            this.prev.removeEventListener('click', this.onPrev);
        }
        /* istanbul ignore else */
        if (this.next) {
            this.next.removeEventListener('click', this.onNext);
        }
    }

    transition(dir) {
        const {transitionDuration, images, index} = this.props;
        const reset = dir === 'reset';
        const updatedIndex = reset ? index : this.getUpdatedIndex(dir, index);
        const preloadIndex = reset ? index : this.getPreloadIndex(dir, index);
        this.setState({
            transitionDirection: dir,
            backgroundSrc: images[updatedIndex].src,
            preloadSrc: images[preloadIndex].src,
            altText: images[updatedIndex].altText,
            panPct: 0
        });
        clearTimeout(this.timeouts.slide);
        this.timeouts.slide = setTimeout(() => {
            clearTimeout(this.timeouts.slide);
            this.setState({transitionDirection: ''});
            if (!reset) {
                if (dir === 'next') {
                    this.props.onNextClick(updatedIndex);
                } else {
                    this.props.onPrevClick(updatedIndex);
                }
            }
        }, transitionDuration);
    }

    onKeyDown = (e) => {
        const {key, target} = e;
        const {onClick} = this.props;
        if (
            this.keyUp &&
            target === this.main &&
            (key === 'Enter' || key === ' ')
        ) {
            this.keyUp = false;
            if (key === ' ') {
                e.preventDefault();
            }
            onClick();
        }
    };

    onKeyUp = (e) => {
        const {key, target} = e;
        if (
            !this.keyUp &&
            target === this.main &&
            (key === 'Enter' || key === ' ')
        ) {
            this.keyUp = true;
        }
    };

    handleTouchEnd = (e) => {
        const {changeThreshold} = this.props;
        const {panPct} = this.state;
        this.x = null;
        this.y = null;
        this.panAxis = null;
        if (this.touchMoved) {
            if (panPct <= -changeThreshold) {
                this.onNext(e);
            } else if (panPct >= changeThreshold) {
                this.onPrev(e);
            } else {
                this.transition('reset');
            }
        }
        this.touchMoved = false;
    };

    handleTouchMove = (e) => {
        const clientX = e.touches[0].clientX;
        const clientY = e.touches[0].clientY;

        if (this.x && this.y) {
            const xDiff = this.x - clientX;
            this.touchMoved = true;
            // determine pan axis for this swipe
            /* istanbul ignore else */
            if (!this.panAxis) {
                const yDiffAbs = Math.abs(this.y - clientY);
                this.panAxis = Math.abs(xDiff) > yDiffAbs ? 'horz' : 'vert';
            }
            /* Get the start position and direction */
            if (this.panAxis === 'horz') {
                /* Prevent vertical scrolling while are are swiping on the image */
                e.preventDefault();
                const width = e.currentTarget.clientWidth;
                const panPct = Math.round(-(xDiff / width) * 100);
                this.setState({
                    panPct: Math.min(Math.max(-100, panPct), 100)
                });
            }
        } else {
            // essentially touch start
            this.x = clientX;
            this.y = clientY;
        }
    };

    onChangeIndex(dir, e) {
        e.stopPropagation();
        e.preventDefault();
        this.transition(dir);
    }

    getNextIndex(index) {
        const {images} = this.props;
        return index > images.length - 2 ? 0 : index + 1;
    }

    getPrevIndex(index) {
        const {images} = this.props;
        return index > 0 ? index - 1 : images.length - 1;
    }

    getUpdatedIndex(dir, index) {
        return dir === 'next'
            ? this.getNextIndex(index)
            : this.getPrevIndex(index);
    }

    getPreloadIndex(dir, index) {
        const val = this.getUpdatedIndex(dir, this.getUpdatedIndex(dir, index));
        return val;
    }

    getTransitionDuration(dir, image) {
        const {transitionDuration} = this.props;
        if (
            dir === image ||
            dir === 'reset' ||
            (dir !== '' && image === 'either')
        ) {
            return `${transitionDuration}ms`;
        }
        return '';
    }

    setRef(name, ref) {
        const {onClick} = this.props;
        const methodsMap = {
            prev: this.onPrev,
            next: this.onNext,
            main: onClick
        };
        this[name] = ref;
        if (ref && methodsMap[name]) {
            ref.addEventListener('click', methodsMap[name]);
            if (name === 'main') {
                ref.addEventListener('keydown', this.onKeyDown);
                ref.addEventListener('keyup', this.onKeyUp);
                ref.addEventListener('touchmove', this.handleTouchMove);
                ref.addEventListener('touchend', this.handleTouchEnd);
            }
        }
    }

    appendBackgroundStyle = (style, imageUrl) => {
        if (imageUrl) {
            style.backgroundImage = `url(${imageUrl})`;
        }

        return style;
    };

    render() {
        const {
            transitionDirection,
            preloadSrc,
            backgroundSrc,
            panPct
        } = this.state;
        const {
            index,
            images,
            transitionStyle,
            renderButtons,
            className,
            tabIndex,
            href,
            target
        } = this.props;
        const backgroundImageSrc = (images[index] && images[index].src) || null;
        const nextBackgroundImage = images[this.getNextIndex(index)];
        const nextBackgroundImageSrc =
            (nextBackgroundImage && nextBackgroundImage.src) || null;
        const prevBackgroundImage = images[this.getPrevIndex(index)];
        const prevBackgroundImageSrc =
            (prevBackgroundImage && prevBackgroundImage.src) || null;
        const rootClass = classnames('SimpleImageCarousel', {
            'SimpleImageCarousel--transitioning': !!transitionDirection,
            [className]: !!className
        });
        const curClass = classnames(
            'SimpleImageCarousel__image SimpleImageCarousel__image--cur',
            {
                'SimpleImageCarousel__image--transitioning-prev-slide':
                    transitionDirection === 'prev' &&
                    transitionStyle === 'slide',
                'SimpleImageCarousel__image--transitioning-next-slide':
                    transitionDirection === 'next' &&
                    transitionStyle === 'slide'
            }
        );

        const curStyle = this.appendBackgroundStyle(
            {
                transitionDuration: this.getTransitionDuration(
                    transitionDirection,
                    'either'
                ),
                ...getPanningStyle(transitionStyle, panPct).cur
            },
            backgroundImageSrc
        );

        const rootStyle = this.appendBackgroundStyle({}, backgroundSrc);

        const nextClass = classnames('SimpleImageCarousel__image', {
            'SimpleImageCarousel__image--next-slide':
                transitionStyle === 'slide' || transitionStyle === 'slideOver',
            'SimpleImageCarousel__image--next-fade': transitionStyle === 'fade',
            'SimpleImageCarousel__image--transitioning-slide':
                transitionDirection === 'next' &&
                (transitionStyle === 'slide' ||
                    transitionStyle === 'slideOver'),
            'SimpleImageCarousel__image--transitioning-fade':
                transitionDirection === 'next' && transitionStyle === 'fade'
        });
        const nextStyle = this.appendBackgroundStyle(
            {
                transitionDuration: this.getTransitionDuration(
                    transitionDirection,
                    'next'
                ),
                ...getPanningStyle(transitionStyle, panPct).next
            },
            nextBackgroundImageSrc
        );
        const prevClass = classnames('SimpleImageCarousel__image', {
            'SimpleImageCarousel__image--prev-slide':
                transitionStyle === 'slide' || transitionStyle === 'slideOver',
            'SimpleImageCarousel__image--prev-fade': transitionStyle === 'fade',
            'SimpleImageCarousel__image--transitioning-slide':
                transitionDirection === 'prev' &&
                (transitionStyle === 'slide' ||
                    transitionStyle === 'slideOver'),
            'SimpleImageCarousel__image--transitioning-fade':
                transitionDirection === 'prev' && transitionStyle === 'fade'
        });
        const prevStyle = this.appendBackgroundStyle(
            {
                transitionDuration: this.getTransitionDuration(
                    transitionDirection,
                    'prev'
                ),
                ...getPanningStyle(transitionStyle, panPct).prev
            },
            prevBackgroundImageSrc
        );
        const preloadStyle = this.appendBackgroundStyle({}, preloadSrc);
        return (
            <a
                aria-label={'View property'}
                className={rootClass}
                data-imgsrcurl={backgroundSrc}
                data-index={index}
                data-wdio="SimpleImageCarousel--main"
                ref={this.setMainRef}
                role="button"
                style={rootStyle}
                tabIndex={tabIndex}
                target={target}
                href={href}
            >
                {images.length > 1 && (
                    <div>
                        <div
                            className={curClass}
                            style={curStyle}
                            role="img"
                            aria-label={this.state.altText}
                        />
                        <div className={nextClass} style={nextStyle} />
                        <div className={prevClass} style={prevStyle} />
                        <div
                            className="SimpleImageCarousel__preloader"
                            style={preloadStyle}
                        />
                        {renderButtons({
                            setPrevRef: this.setPrevRef,
                            setNextRef: this.setNextRef
                        })}
                    </div>
                )}
            </a>
        );
    }
}
export default SimpleImageCarousel;
